import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight, Terminal, Mail, Github, Linkedin, Download, FileText } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const GLITCH_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*<>/\\";

function GlitchText({ text }: { text: string }) {
  const [display, setDisplay] = useState(text);
  const [hovering, setHovering] = useState(false);
  const frameRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Update display when text prop changes (e.g. language switch)
  useEffect(() => {
    setDisplay(text);
  }, [text]);

  const scramble = () => {
    let i = 0;
    if (frameRef.current) clearInterval(frameRef.current);
    frameRef.current = setInterval(() => {
      setDisplay(
        text
          .split("")
          .map((ch, idx) => {
            if (ch === " ") return " ";
            if (idx < i) return ch;
            return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
          })
          .join("")
      );
      i += 0.6;
      if (i > text.length) {
        if (frameRef.current) clearInterval(frameRef.current);
        setDisplay(text);
        setHovering(false);
      }
    }, 25);
  };

  const handleEnter = () => {
    setHovering(true);
    scramble();
  };

  useEffect(() => () => { if (frameRef.current) clearInterval(frameRef.current); }, []);

  return (
    <span onMouseEnter={handleEnter} style={{ display: "inline-block", cursor: "none" }}>
      {display}
    </span>
  );
}

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export function CTA({ personalInfo, language = "fr" }: { personalInfo?: any, language?: string }) {
  const { ref, visible } = useInView(0.1);
  const [btnHover, setBtnHover] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const btnRef = useRef<HTMLAnchorElement>(null);

  // Magnetic button effect
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) * 0.3;
    const dy = (e.clientY - cy) * 0.3;
    setMousePos({ x: dx, y: dy });
  };

  const handleMouseLeave = () => {
    setBtnHover(false);
    setMousePos({ x: 0, y: 0 });
  };

  return (
    <section
      ref={ref}
      className="relative py-36 md:py-48 overflow-hidden flex flex-col items-center justify-center text-center"
      style={{ background: "#07070b" }}
    >
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(34,211,238,1) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,1) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Big radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 50% 50%, rgba(34,211,238,0.06) 0%, transparent 70%)",
        }}
      />

      {/* Corner decorations */}
      <div
        className="absolute top-8 left-8 pointer-events-none"
        style={{
          width: "60px",
          height: "60px",
          borderTop: "1px solid rgba(34,211,238,0.3)",
          borderLeft: "1px solid rgba(34,211,238,0.3)",
        }}
      />
      <div
        className="absolute bottom-8 right-8 pointer-events-none"
        style={{
          width: "60px",
          height: "60px",
          borderBottom: "1px solid rgba(34,211,238,0.3)",
          borderRight: "1px solid rgba(34,211,238,0.3)",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-8"
        >
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full"
            style={{
              background: "rgba(34,211,238,0.08)",
              border: "1px solid rgba(34,211,238,0.2)",
            }}
          >
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: "#22d3ee", boxShadow: "0 0 6px #22d3ee" }}
            />
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "11px",
                color: "#22d3ee",
                letterSpacing: "0.12em",
              }}
            >
              {language === "en" ? "AVAILABLE FOR OPPORTUNITIES" : "DISPONIBLE AUX OPPORTUNITÉS"}
            </span>
          </div>
        </motion.div>

        {/* Big headline with glitch */}
        <div className="mb-6 overflow-hidden">
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={visible ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(40px, 8vw, 110px)",
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1,
              letterSpacing: "-0.03em",
            }}
          >
            <GlitchText text={language === "en" ? "READY TO" : "PRÊT À"} />
          </motion.div>
        </div>
        <div className="mb-8 overflow-hidden">
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={visible ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(40px, 8vw, 110px)",
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "-0.03em",
              background: "linear-gradient(135deg, #22d3ee, #a78bfa)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            <GlitchText text={language === "en" ? "COLLABORATE" : "COLLABORER"} />
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.6 }}
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(15px, 2vw, 18px)",
            color: "rgba(255,255,255,0.45)",
            maxWidth: "500px",
            margin: "0 auto 48px",
            lineHeight: 1.75,
          }}
        >
          {language === "en" ? "Ready to start a project? Got an idea? Or just want to take a closer look at my code in the editor?" : "Prêt à démarrer un projet ? Une idée ? Ou juste envie de voir mon code de plus près dans l'éditeur ?"}
        </motion.p>

        {/* Magnetic CTA button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            ref={btnRef}
            href={`/dev?lang=${language}`}
            onMouseEnter={() => setBtnHover(true)}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className="group relative flex items-center gap-3 px-9 py-4 rounded-full overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #22d3ee, #a78bfa)",
              color: "#050508",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "16px",
              fontWeight: 700,
              boxShadow: btnHover
                ? "0 0 60px rgba(34,211,238,0.5), 0 0 120px rgba(34,211,238,0.2)"
                : "0 0 30px rgba(34,211,238,0.25)",
              transform: `translate(${mousePos.x}px, ${mousePos.y}px)`,
              transition: "box-shadow 0.3s ease, transform 0.1s ease",
              cursor: "none",
            }}
          >
            <Terminal size={18} />
            <span>{language === "en" ? "Open Editor" : "Ouvrir l'éditeur"}</span>
            <motion.div
              animate={{ x: btnHover ? 4 : 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <ArrowRight size={18} />
            </motion.div>
          </a>

          <a
            href={personalInfo?.email ? `mailto:${personalInfo.email}` : "mailto:contact@samicakiral.com"}
            className="flex items-center gap-2 px-7 py-4 rounded-full transition-all"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.6)",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              cursor: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(34,211,238,0.3)";
              e.currentTarget.style.color = "#22d3ee";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
              e.currentTarget.style.color = "rgba(255,255,255,0.6)";
            }}
          >
            <Mail size={16} />
            {language === "en" ? "Contact me" : "Me contacter"}
          </a>
        </motion.div>

        {/* CV PDF Preview & Download */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={visible ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.6, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-20 mb-10 flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16 w-full max-w-4xl mx-auto p-6 md:p-12 rounded-3xl relative overflow-hidden"
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
          }}
        >
          {/* Subtle glow background */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at center, rgba(34,211,238,0.05) 0%, transparent 70%)" }} />

          {/* Left Text */}
          <div className="flex-1 text-center md:text-left z-10">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "-0.02em" }}>
              {language === "en" ? "Looking for a classic format?" : "Vous préférez un format classique ?"}
            </h3>
            <p className="text-white/50 mb-8 leading-relaxed text-sm md:text-base max-w-md mx-auto md:mx-0" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {language === "en"
                ? "You can download a clean, ATS-friendly PDF version of my resume. It is dynamically generated in LaTeX to ensure pixel-perfect typography, directly translated into the selected language."
                : "Vous pouvez également télécharger une version PDF propre de mon CV. Il est généré dynamiquement en LaTeX pour une typographie irréprochable et adapté à la langue sélectionnée."}
            </p>

            <a
              href={`${API_BASE_URL}/generate-latex-pdf/?lang=${language}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-xl transition-all group"
              style={{
                background: "rgba(34,211,238,0.1)",
                border: "1px solid rgba(34,211,238,0.3)",
                color: "#22d3ee",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                fontSize: "15px",
                cursor: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(34,211,238,0.2)";
                e.currentTarget.style.boxShadow = "0 0 20px rgba(34,211,238,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(34,211,238,0.1)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FileText size={18} className="group-hover:scale-110 transition-transform" />
              {language === "en" ? "Download PDF Version" : "Obtenir la version PDF"}
              <Download size={14} className="ml-2 opacity-50" />
            </a>
          </div>

          {/* Right Preview */}
          <div className="hidden md:block relative group rounded-xl overflow-hidden flex-shrink-0 z-10 bg-white" style={{
            width: "180px",
            height: "254px", // A4 proportions
            boxShadow: "0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.15)"
          }}>
            <iframe
              src={`${API_BASE_URL}/generate-latex-pdf/?lang=${language}#toolbar=0&navpanes=0&scrollbar=0`}
              title="CV Preview"
              className="w-full h-full border-0 pointer-events-none"
              scrolling="no"
              style={{ overflow: 'hidden' }}
            />
            {/* Hover Download Overlay */}
            <a
              href={`${API_BASE_URL}/generate-latex-pdf/?lang=${language}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 bg-[#050508]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center backdrop-blur-sm cursor-none text-[#22d3ee]"
            >
              <Download size={32} className="mb-3 transform group-hover:translate-y-1 transition-transform" />
              <span className="text-xs font-bold tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {language === "en" ? "CLICK TO DOWNLOAD" : "TÉLÉCHARGER"}
              </span>
            </a>
          </div>
        </motion.div>

        {/* Social links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="flex items-center justify-center gap-4 mt-12"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "32px" }}
        >
          {[
            { icon: Github, label: "GitHub", href: personalInfo?.github_url || "#" },
            { icon: Linkedin, label: "LinkedIn", href: personalInfo?.linkedin_url || "#" },
            { icon: Mail, label: "Email", href: personalInfo?.email ? `mailto:${personalInfo.email}` : "mailto:contact@samicakiral.com" },
          ].map(({ icon: Icon, label, href }) => (
            <a
              key={label}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "12px",
                color: "rgba(255,255,255,0.35)",
                cursor: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#22d3ee";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.35)";
              }}
            >
              <Icon size={14} />
              {label}
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
